import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { format, subDays } from "date-fns";
import {
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    AreaChart,
    Area,
} from "recharts";
import {
    getBusinessById,
    getEmployeeByBusiness,
    getTipsByBusiness,
    getPayoutsByBusiness,
} from "../../services/firestoreService";
import { useAuth } from "../../context/AuthProvider";
import BusinessLayoutV2 from "../../components/BusinessLayoutV2";
import BusinessOnboardingPanel from "../../components/BusinessOnboardingPanel";

const FILTER_OPTIONS = {
    "Last 24 hours": 1,
    "Last 7 days": 7,
    "Last 14 days": 14,
    "Last 30 days": 30,
    "Last 90 days": 90,
    "Last 180 days": 180,
    "Last year": 365,
    "All time": null,
};

const FILTER_LABELS = Object.keys(FILTER_OPTIONS);

const ChevronDown = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 6l4 4 4-4" />
    </svg>
);

const StarIcon = ({ filled }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill={filled ? "#e8a20e" : "none"} stroke={filled ? "#e8a20e" : "#d4d4d4"} strokeWidth="2">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
);

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div style={{ background: "#fff", border: "1px solid #e8e8ee", borderRadius: 12, padding: "10px 16px", boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }}>
            <p style={{ color: "#8b8b9e", fontSize: 12, margin: 0 }}>{label}</p>
            <p style={{ color: "#0d7c5f", fontSize: 18, fontWeight: 700, margin: "2px 0 0" }}>€{Number(payload[0].value || 0).toFixed(2)}</p>
        </div>
    );
};

const getTimestampDate = (value) => {
    if (!value) return null;
    if (value?.seconds) return new Date(value.seconds * 1000);
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export default function BusinessDashboardPageV2() {
    const { userData } = useAuth();

    const [business, setBusiness] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [tips, setTips] = useState([]);
    const [payouts, setPayouts] = useState([]);

    const [filter, setFilter] = useState("Last 30 days");
    const [filterOpen, setFilterOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("tips");

    const filterRef = useRef(null);

    const filterDays = FILTER_OPTIONS[filter];

    useEffect(() => {
        const handler = (e) => {
            if (filterRef.current && !filterRef.current.contains(e.target)) {
                setFilterOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (!userData?.businessID) return;

            const [businessData, employeeData, tipsData, payoutsData] = await Promise.all([
                getBusinessById(userData.businessID),
                getEmployeeByBusiness(userData.businessID),
                getTipsByBusiness(userData.businessID),
                getPayoutsByBusiness(userData.businessID),
            ]);

            const teamEnabled = businessData?.config?.teamEnabled ?? false;
            const teamEmployee = teamEnabled ? [{ id: "team", displayName: "Team Tip Pool" }] : [];

            setBusiness(businessData || null);
            setEmployees([...(employeeData || []), ...teamEmployee]);
            setTips(Array.isArray(tipsData) ? tipsData : []);
            setPayouts(Array.isArray(payoutsData) ? payoutsData : []);
        };

        fetchData();
    }, [userData]);

    const filteredTips = useMemo(() => {
        if (!filterDays) return tips;
        const boundary = subDays(new Date(), filterDays);
        return tips.filter((tip) => {
            const tipDate = getTimestampDate(tip.createdAt);
            return tipDate ? tipDate >= boundary : false;
        });
    }, [tips, filterDays]);

    const filteredPayouts = useMemo(() => {
        if (!filterDays) return payouts;
        const boundary = subDays(new Date(), filterDays);
        return payouts.filter((payout) => {
            const payoutDate = getTimestampDate(payout.createdAt);
            return payoutDate ? payoutDate >= boundary : false;
        });
    }, [payouts, filterDays]);

    const totalTips = filteredTips.reduce((sum, tip) => sum + Number(tip.amount || 0), 0);
    const totalPayouts = filteredPayouts.reduce((sum, payout) => sum + Number(payout.totalAmount || 0), 0);
    const tipCount = filteredTips.length;
    const employeeCount = employees.filter((e) => e.id !== "team").length;

    const ratings = filteredTips
        .map((tip) => Number(tip.rating))
        .filter((rating) => !Number.isNaN(rating));
    const avgRating = ratings.length ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1) : "0.0";

    const uniqueDays = new Set(
        filteredTips
            .map((tip) => getTimestampDate(tip.createdAt))
            .filter(Boolean)
            .map((date) => date.toDateString())
    );
    const avgTipsPerDay = uniqueDays.size ? (totalTips / uniqueDays.size).toFixed(2) : "0.00";

    const teamEnabled = business?.config?.teamEnabled ?? false;
    const personalEnabled = business?.config?.personalEnabled ?? false;
    const teamOnly = teamEnabled && !personalEnabled;

    const topServer = useMemo(() => {
        if (teamOnly) {
            const teamTips = filteredTips.filter((tip) => tip.employeeID === "team");
            const days = new Set(
                teamTips
                    .map((tip) => getTimestampDate(tip.createdAt))
                    .filter(Boolean)
                    .map((date) => date.toDateString())
            );
            const avg = days.size ? teamTips.reduce((sum, tip) => sum + Number(tip.amount || 0), 0) / days.size : 0;
            return { name: "Team", avg };
        }

        const ranked = employees
            .filter((emp) => emp.id !== "team")
            .map((emp) => {
                const empTips = filteredTips.filter((tip) => tip.employeeID === emp.id);
                const days = new Set(
                    empTips
                        .map((tip) => getTimestampDate(tip.createdAt))
                        .filter(Boolean)
                        .map((date) => date.toDateString())
                );
                const avg = days.size ? empTips.reduce((sum, tip) => sum + Number(tip.amount || 0), 0) / days.size : 0;
                return { name: emp.displayName || "Unknown", avg };
            })
            .sort((a, b) => b.avg - a.avg);

        return ranked[0] || { name: "NA", avg: 0 };
    }, [teamOnly, filteredTips, employees]);

    const pendingByEmployee = useMemo(() => {
        return employees
            .map((employee) => {
                const empTips = tips.filter((tip) => tip.employeeID === employee.id).reduce((sum, tip) => sum + Number(tip.amount || 0), 0);
                const empPayouts = payouts
                    .filter((payout) => payout.employeeID === employee.id)
                    .reduce((sum, payout) => sum + Number(payout.totalAmount || 0), 0);
                const pending = empTips - empPayouts;
                return { employeeID: employee.id, name: employee.displayName || "Unknown", pending };
            })
            .filter((item) => item.pending > 0)
            .sort((a, b) => b.pending - a.pending);
    }, [employees, tips, payouts]);

    const chartData = useMemo(() => {
        if (!filteredTips.length) return [];

        if (filterDays === 1) {
            const hourlyMap = {};
            for (let i = 0; i < 24; i += 1) {
                hourlyMap[`${String(i).padStart(2, "0")}:00`] = 0;
            }
            filteredTips.forEach((tip) => {
                const date = getTimestampDate(tip.createdAt);
                if (!date) return;
                const label = `${String(date.getHours()).padStart(2, "0")}:00`;
                hourlyMap[label] += Number(tip.amount || 0);
            });
            return Object.entries(hourlyMap).map(([date, amount]) => ({ date, amount: Number(amount.toFixed(2)) }));
        }

        const dailyTipMap = {};
        const totalDays = filterDays || 30;
        for (let i = totalDays - 1; i >= 0; i -= 1) {
            const date = subDays(new Date(), i);
            dailyTipMap[format(date, "MMM d")] = 0;
        }

        filteredTips.forEach((tip) => {
            const date = getTimestampDate(tip.createdAt);
            if (!date) return;
            const label = format(date, "MMM d");
            if (dailyTipMap[label] !== undefined) {
                dailyTipMap[label] += Number(tip.amount || 0);
            }
        });

        return Object.entries(dailyTipMap).map(([date, amount]) => ({ date, amount: Number(amount.toFixed(2)) }));
    }, [filteredTips, filterDays]);

    const recentTips = useMemo(
        () => [...filteredTips].sort((a, b) => (getTimestampDate(b.createdAt) || 0) - (getTimestampDate(a.createdAt) || 0)).slice(0, 5),
        [filteredTips]
    );

    const recentPayouts = useMemo(
        () => [...filteredPayouts].sort((a, b) => (getTimestampDate(b.createdAt) || 0) - (getTimestampDate(a.createdAt) || 0)).slice(0, 5),
        [filteredPayouts]
    );

    return (
        <BusinessLayoutV2>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=JetBrains+Mono:wght@400;500&display=swap');

                .business-dashboard-v2 * { box-sizing: border-box; margin: 0; padding: 0; }
                .business-dashboard-v2 {
                    --bg: #f6f6f9;
                    --surface: #ffffff;
                    --surface-raised: #f0f0f5;
                    --border: #e8e8ee;
                    --border-bright: #d4d4dd;
                    --text-primary: #1a1a2e;
                    --text-secondary: #6b6b82;
                    --text-muted: #9b9bb0;
                    --accent: #0d7c5f;
                    --accent-light: #e8f5f0;
                    --green: #0d7c5f;
                    --green-light: #e8f5f0;
                    --blue: #3b6fe0;
                    --blue-light: #eef3fc;
                    --amber: #c47a0a;
                    --amber-light: #fef6e6;
                    --font: 'DM Sans', -apple-system, sans-serif;
                    --mono: 'JetBrains Mono', monospace;
                    font-family: var(--font);
                    background: var(--bg);
                    color: var(--text-primary);
                    min-height: 100vh;
                }
                .business-dashboard-v2 .dashboard {
                    width: 100%;
                    margin: 0 auto;
                    padding: 32px 0 80px;
                }
                .business-dashboard-v2 .header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 24px;
                    gap: 12px;
                    flex-wrap: wrap;
                }
                .business-dashboard-v2 .header h1 { font-size: 28px; font-weight: 700; letter-spacing: -0.5px; }
                .business-dashboard-v2 .header p { margin-top: 4px; color: var(--text-secondary); font-size: 14px; }
                .business-dashboard-v2 .see-more {
                    color: var(--accent);
                    font-size: 13px;
                    font-weight: 500;
                    text-decoration: none;
                    padding: 8px 14px;
                    border: 1px solid var(--accent);
                    border-radius: 8px;
                }
                .business-dashboard-v2 .see-more:hover { background: var(--accent-light); }
                .business-dashboard-v2 .filter-wrapper { position: relative; margin-bottom: 28px; }
                .business-dashboard-v2 .filter-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 10px;
                    padding: 10px 16px;
                    font-family: var(--font);
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    color: var(--text-primary);
                }
                .business-dashboard-v2 .filter-btn:hover { border-color: var(--border-bright); background: var(--surface-raised); }
                .business-dashboard-v2 .filter-dropdown {
                    position: absolute;
                    top: calc(100% + 6px);
                    left: 0;
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 12px;
                    padding: 6px;
                    z-index: 40;
                    min-width: 220px;
                    box-shadow: 0 12px 40px rgba(0,0,0,0.1);
                }
                .business-dashboard-v2 .filter-option {
                    width: 100%;
                    border: none;
                    background: none;
                    text-align: left;
                    padding: 10px 14px;
                    border-radius: 8px;
                    font-size: 13px;
                    color: var(--text-secondary);
                    cursor: pointer;
                }
                .business-dashboard-v2 .filter-option:hover { background: var(--surface-raised); color: var(--text-primary); }
                .business-dashboard-v2 .filter-option.active { background: var(--accent-light); color: var(--accent); font-weight: 500; }
                .business-dashboard-v2 .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 14px;
                    margin-bottom: 28px;
                }
                @media (max-width: 700px) {
                    .business-dashboard-v2 .stats-grid { grid-template-columns: repeat(2, 1fr); }
                }
                .business-dashboard-v2 .stat-card {
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 16px;
                    padding: 20px;
                    display: flex;
                    flex-direction: column;
                    gap: 14px;
                }
                .business-dashboard-v2 .icon-dot {
                    width: 36px;
                    height: 36px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 16px;
                }
                .business-dashboard-v2 .stat-card .label {
                    font-size: 12px;
                    font-weight: 500;
                    color: var(--text-muted);
                    text-transform: uppercase;
                    letter-spacing: 0.8px;
                }
                .business-dashboard-v2 .stat-card .value {
                    font-size: 28px;
                    font-weight: 700;
                    font-family: var(--mono);
                    letter-spacing: -1px;
                    line-height: 1;
                }
                .business-dashboard-v2 .stat-card .suffix { font-size: 13px; color: var(--text-muted); margin-top: 2px; }
                .business-dashboard-v2 .secondary-stats {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 14px;
                    margin-bottom: 32px;
                }
                @media (max-width: 700px) {
                    .business-dashboard-v2 .secondary-stats { grid-template-columns: 1fr; }
                }
                .business-dashboard-v2 .sec-stat {
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 14px;
                    padding: 18px 20px;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                }
                .business-dashboard-v2 .icon-wrap {
                    width: 42px;
                    height: 42px;
                    border-radius: 11px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    flex-shrink: 0;
                }
                .business-dashboard-v2 .sec-label { font-size: 12px; color: var(--text-muted); margin-bottom: 2px; }
                .business-dashboard-v2 .sec-value { font-size: 18px; font-weight: 600; font-family: var(--mono); }
                .business-dashboard-v2 .chart-section {
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 18px;
                    padding: 24px;
                    margin-bottom: 28px;
                }
                .business-dashboard-v2 .chart-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                }
                .business-dashboard-v2 .chart-header h2 { font-size: 16px; font-weight: 600; }
                .business-dashboard-v2 .chart-total { font-family: var(--mono); font-size: 14px; color: var(--accent); font-weight: 500; }
                .business-dashboard-v2 .pending-banner {
                    background: var(--amber-light);
                    border: 1px solid #f0deb3;
                    border-radius: 18px;
                    padding: 24px;
                    margin-bottom: 28px;
                }
                .business-dashboard-v2 .pending-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 16px;
                }
                .business-dashboard-v2 .pending-total {
                    font-family: var(--mono);
                    font-size: 22px;
                    font-weight: 700;
                    color: var(--amber);
                }
                .business-dashboard-v2 .pending-list {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .business-dashboard-v2 .pending-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 10px 14px;
                    background: rgba(255,255,255,0.7);
                    border-radius: 10px;
                    font-size: 13px;
                    border: 1px solid #f0deb3;
                }
                .business-dashboard-v2 .pending-name { font-weight: 500; color: var(--text-primary); }
                .business-dashboard-v2 .tabs-header {
                    display: flex;
                    border-bottom: 2px solid var(--border);
                }
                .business-dashboard-v2 .tab-btn {
                    padding: 12px 24px;
                    background: none;
                    border: none;
                    color: var(--text-muted);
                    font-family: var(--font);
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    position: relative;
                }
                .business-dashboard-v2 .tab-btn.active { color: var(--accent); }
                .business-dashboard-v2 .tab-btn.active::after {
                    content: '';
                    position: absolute;
                    bottom: -2px;
                    left: 0;
                    right: 0;
                    height: 2px;
                    background: var(--accent);
                }
                .business-dashboard-v2 .list-section {
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-top: none;
                    border-radius: 0 0 18px 18px;
                    overflow: hidden;
                }
                .business-dashboard-v2 .list-item {
                    padding: 16px 20px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border-bottom: 1px solid var(--border);
                    gap: 14px;
                }
                .business-dashboard-v2 .list-item:last-child { border-bottom: none; }
                .business-dashboard-v2 .left { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
                .business-dashboard-v2 .amount { font-family: var(--mono); font-weight: 600; font-size: 15px; }
                .business-dashboard-v2 .feedback {
                    font-size: 13px;
                    color: var(--text-secondary);
                    max-width: 420px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .business-dashboard-v2 .right { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; flex-shrink: 0; }
                .business-dashboard-v2 .date { font-size: 12px; color: var(--text-muted); }
                .business-dashboard-v2 .status-badge {
                    font-size: 11px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 3px 10px;
                    border-radius: 6px;
                }
                .business-dashboard-v2 .status-paid { color: var(--green); background: var(--green-light); }
                .business-dashboard-v2 .status-pending { color: var(--amber); background: var(--amber-light); }
                .business-dashboard-v2 .see-all {
                    display: block;
                    text-align: center;
                    padding: 14px;
                    color: var(--text-muted);
                    font-size: 13px;
                    font-weight: 500;
                    text-decoration: none;
                    width: 100%;
                    border: none;
                    background: none;
                }
                .business-dashboard-v2 .see-all:hover { color: var(--accent); }
                .business-dashboard-v2 .empty-row { padding: 16px 20px; color: var(--text-secondary); font-size: 13px; }
            `}</style>

            <div className="business-dashboard-v2">
                <div className="dashboard">
                    <header className="header">
                        <div>
                            <h1>{business?.displayName || business?.name || "Business"} Dashboard</h1>
                            <p>Overview of tips, payouts and team performance</p>
                        </div>
                        <Link className="see-more" to="/business/statistics">Detailed Statistics</Link>
                    </header>

                    <BusinessOnboardingPanel
                        business={business}
                        employees={employees}
                        onStatusChange={(onboardingStatus) => setBusiness((current) => ({...current, onboardingStatus}))}
                    />

                    <div className="filter-wrapper" ref={filterRef}>
                        <button className="filter-btn" onClick={() => setFilterOpen((prev) => !prev)} type="button">
                            {filter} <ChevronDown />
                        </button>
                        {filterOpen && (
                            <div className="filter-dropdown">
                                {FILTER_LABELS.map((label) => (
                                    <button
                                        key={label}
                                        className={`filter-option ${label === filter ? "active" : ""}`}
                                        onClick={() => {
                                            setFilter(label);
                                            setFilterOpen(false);
                                        }}
                                        type="button"
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="icon-dot" style={{ background: "var(--green-light)" }}>💰</div>
                            <span className="label">Total Tips</span>
                            <div>
                                <span className="value" style={{ color: "var(--green)" }}>€{totalTips.toFixed(2)}</span>
                                <div className="suffix">{filter.toLowerCase()}</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="icon-dot" style={{ background: "var(--blue-light)" }}>✓</div>
                            <span className="label">Payouts Made</span>
                            <div>
                                <span className="value" style={{ color: "var(--blue)" }}>€{totalPayouts.toFixed(2)}</span>
                                <div className="suffix">{filteredPayouts.length} payouts</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="icon-dot" style={{ background: "var(--surface-raised)" }}>🧾</div>
                            <span className="label">Tips Count</span>
                            <div>
                                <span className="value">{tipCount}</span>
                                <div className="suffix">transactions</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="icon-dot" style={{ background: "var(--amber-light)" }}>👥</div>
                            <span className="label">Employees</span>
                            <div>
                                <span className="value" style={{ color: "var(--amber)" }}>{employeeCount}</span>
                                <div className="suffix">active records</div>
                            </div>
                        </div>
                    </div>

                    <div className="secondary-stats">
                        <div className="sec-stat">
                            <div className="icon-wrap" style={{ background: "var(--green-light)" }}>💸</div>
                            <div>
                                <div className="sec-label">Avg tips per day</div>
                                <div className="sec-value" style={{ color: "var(--green)" }}>€{avgTipsPerDay}</div>
                            </div>
                        </div>
                        <div className="sec-stat">
                            <div className="icon-wrap" style={{ background: "var(--amber-light)" }}>⭐</div>
                            <div>
                                <div className="sec-label">Avg rating</div>
                                <div className="sec-value" style={{ color: "var(--amber)" }}>{avgRating}/5</div>
                            </div>
                        </div>
                        <div className="sec-stat">
                            <div className="icon-wrap" style={{ background: "var(--blue-light)" }}>👑</div>
                            <div>
                                <div className="sec-label">Top performer (daily avg)</div>
                                <div className="sec-value" style={{ color: "var(--blue)" }}>{topServer.name} · €{topServer.avg.toFixed(2)}</div>
                            </div>
                        </div>
                    </div>

                    <div className="chart-section">
                        <div className="chart-header">
                            <h2>Tips Trend</h2>
                            <span className="chart-total">€{totalTips.toFixed(2)} total</span>
                        </div>
                        <ResponsiveContainer width="100%" height={220}>
                            <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="bizTipGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#0d7c5f" stopOpacity={0.15} />
                                        <stop offset="100%" stopColor="#0d7c5f" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="#eeeef2" vertical={false} />
                                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9b9bb0" }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 11, fill: "#9b9bb0" }} axisLine={false} tickLine={false} tickFormatter={(v) => `€${v}`} width={42} />
                                <Tooltip content={<CustomTooltip />} />
                                <Area type="monotone" dataKey="amount" stroke="#0d7c5f" strokeWidth={2.5} fill="url(#bizTipGrad)" dot={false} activeDot={{ r: 5, stroke: "#0d7c5f", strokeWidth: 2, fill: "#fff" }} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="pending-banner">
                        <div className="pending-header">
                            <h3>⏳ Pending Payouts</h3>
                            <span className="pending-total">€{pendingByEmployee.reduce((sum, item) => sum + item.pending, 0).toFixed(2)}</span>
                        </div>
                        <div className="pending-list">
                            {pendingByEmployee.length ? (
                                pendingByEmployee.slice(0, 5).map((item) => (
                                    <div key={item.employeeID} className="pending-item">
                                        <span className="pending-name">{item.name}</span>
                                        <span>€{item.pending.toFixed(2)}</span>
                                    </div>
                                ))
                            ) : (
                                <div className="pending-item">
                                    <span className="pending-name">No pending payouts.</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="tabs-container">
                        <div className="tabs-header">
                            <button className={`tab-btn ${activeTab === "tips" ? "active" : ""}`} onClick={() => setActiveTab("tips")} type="button">Recent Tips</button>
                            <button className={`tab-btn ${activeTab === "payouts" ? "active" : ""}`} onClick={() => setActiveTab("payouts")} type="button">Recent Payouts</button>
                        </div>

                        <div className="list-section">
                            {activeTab === "tips" && (
                                <>
                                    {recentTips.length ? (
                                        recentTips.map((tip) => (
                                            <div key={tip.id} className="list-item">
                                                <div className="left">
                                                    <span className="amount" style={{ color: "var(--green)" }}>+€{Number(tip.amount || 0).toFixed(2)}</span>
                                                    <span className="feedback">{tip.feedback || "No feedback"}</span>
                                                </div>
                                                <div className="right">
                                                    <div style={{ display: "flex", gap: 1 }}>
                                                        {[1, 2, 3, 4, 5].map((star) => (
                                                            <StarIcon key={star} filled={star <= Number(tip.rating || 0)} />
                                                        ))}
                                                    </div>
                                                    <span className="date">{getTimestampDate(tip.createdAt)?.toLocaleDateString() || "-"}</span>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="empty-row">No tips found.</div>
                                    )}
                                    <Link className="see-all" to="/business/tips">View all tips →</Link>
                                </>
                            )}

                            {activeTab === "payouts" && (
                                <>
                                    {recentPayouts.length ? (
                                        recentPayouts.map((payout) => {
                                            const employee = employees.find((e) => e.id === payout.employeeID);
                                            const status = String(payout.status || "pending").toLowerCase();
                                            const statusClass = status === "paid" ? "status-paid" : "status-pending";
                                            return (
                                                <div key={payout.id} className="list-item">
                                                    <div className="left">
                                                        <span className="amount">€{Number(payout.totalAmount || 0).toFixed(2)}</span>
                                                        <span className="feedback">{employee?.displayName || (payout.employeeID === "team" ? "Team Tip Pool" : "Unknown")}</span>
                                                    </div>
                                                    <div className="right">
                                                        <span className={`status-badge ${statusClass}`}>{status}</span>
                                                        <span className="date">{getTimestampDate(payout.createdAt)?.toLocaleDateString() || "-"}</span>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="empty-row">No payouts found.</div>
                                    )}
                                    <Link className="see-all" to="/business/payouts">View all payouts →</Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </BusinessLayoutV2>
    );
}
